import {
  Edit as EditIcon,
  Folder as FilesIcon,
  CheckCircleOutline as HasReportIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Column,
  DataTable,
  makeColumnsSearchable,
} from "../../../components/common/DataTable/DataTable";
import { SearchableColumn } from "../../../components/common/SearchInputs";
import {
  ClaimResponseDto,
  ClaimStatus,
  ClaimStatusLabels,
  DriverChargedStatus,
  DriverChargedStatusLabels,
  AutoLBType,
  AutoLBTypeLabels,
} from "../../../types/claim";
import { useDeleteClaimMutation } from "../../../api/claimsApi";
import ClaimFilesDialog from "./ClaimFilesDialog";

interface ClaimsTableProps {
  claims: ClaimResponseDto[];
  isLoading: boolean;
  totalCount?: number;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  isLoadingRows?: boolean;
  onLoadMore?: () => void;
  enableSearch?: boolean;
  onSearchChange?: (filters: Record<string, any>) => void;
}

const getStatusColor = (status: ClaimStatus) => {
  switch (status) {
    case ClaimStatus.Closed:
    case ClaimStatus.ClosedDenied:
    case ClaimStatus.ClosedPartiallyPaid:
    case ClaimStatus.ClosedSubro:
    case ClaimStatus.ClosedPdLbOpen:
      return "default";
    case ClaimStatus.InProgress:
    case ClaimStatus.InProgressAddingToMs:
      return "primary";
    case ClaimStatus.PaymentWaiting:
    case ClaimStatus.SupplementWaiting:
    case ClaimStatus.RepairWaiting:
    case ClaimStatus.W8Appraisal:
      return "warning";
    case ClaimStatus.NotStartedYet:
    case ClaimStatus.NeedToOpen:
      return "info";
    case ClaimStatus.DriverCharge:
      return "error";
    default:
      return "default";
  }
};

const getDriverChargedColor = (status: DriverChargedStatus) => {
  switch (status) {
    case DriverChargedStatus.DriverCharged:
      return "error";
    case DriverChargedStatus.PendingCharge:
      return "warning";
    case DriverChargedStatus.NoCharge:
      return "success";
    case DriverChargedStatus.InvoicedOnDriver:
      return "info";
    default:
      return "default";
  }
};

const ClaimsTable: React.FC<ClaimsTableProps> = ({
  claims,
  isLoading,
  totalCount,
  hasNextPage,
  isLoadingMore,
  isLoadingRows,
  onLoadMore,
  enableSearch = false,
  onSearchChange,
}) => {
  const navigate = useNavigate();
  const [filesDialogOpen, setFilesDialogOpen] = useState(false);
  const [selectedClaimId, setSelectedClaimId] = useState<number | null>(null);
  const [deleteTargetClaim, setDeleteTargetClaim] = useState<ClaimResponseDto | null>(null);
  const [deleteClaim, { isLoading: isDeleting }] = useDeleteClaimMutation();

  const handleEditClick = (claimId: number) => {
    navigate(`/claims/${claimId}/edit`);
  };

  const handleViewFiles = (claimId: number) => {
    setSelectedClaimId(claimId);
    setFilesDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetClaim) return;
    await deleteClaim(deleteTargetClaim.id);
    setDeleteTargetClaim(null);
  };

  const truncated = (value: string | null, maxLen = 60) => {
    if (!value) return <Typography sx={{ fontSize: "0.8125rem" }}>-</Typography>;
    const text = value.length > maxLen ? value.slice(0, maxLen) + "…" : value;
    return (
      <Tooltip title={value.length > maxLen ? value : ""} placement="top" arrow>
        <Typography sx={{ fontSize: "0.8125rem" }}>{text}</Typography>
      </Tooltip>
    );
  };

  const formatEstimate = (value: number | null) =>
    value != null
      ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : "-";

  const formatLocation = (row: ClaimResponseDto) => {
    if (row.city && row.state) return `${row.city}, ${row.state}`;
    return row.city || row.state || "-";
  };

  const columns: Column[] = [
    {
      field: "dateOfLoss",
      headerName: "DOL",
      width: 95,
      renderCell: (value: string) => (
        <Typography sx={{ fontSize: "0.8125rem" }}>
          {value ? new Date(value).toLocaleDateString() : "-"}
        </Typography>
      ),
    },
    {
      field: "location",
      headerName: "Location",
      width: 130,
      renderCell: (_: unknown, row: ClaimResponseDto) => (
        <Typography sx={{ fontSize: "0.8125rem" }}>
          {formatLocation(row)}
        </Typography>
      ),
    },
    {
      field: "companyName",
      headerName: "Company",
      width: 130,
      renderCell: (value: string) => (
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "assignedToName",
      headerName: "Assigned To",
      width: 130,
      renderCell: (value: string) => (
        <Typography sx={{ fontSize: "0.8125rem" }}>{value || "-"}</Typography>
      ),
    },
    {
      field: "driverName",
      headerName: "Driver",
      width: 130,
      renderCell: (value: string) => (
        <Typography sx={{ fontSize: "0.8125rem" }}>{value || "-"}</Typography>
      ),
    },
    {
      field: "truckName",
      headerName: "Truck",
      width: 90,
      renderCell: (value: string) => (
        <Typography sx={{ fontSize: "0.8125rem" }}>{value || "-"}</Typography>
      ),
    },
    {
      field: "trailerName",
      headerName: "Trailer",
      width: 90,
      renderCell: (value: string) => (
        <Typography sx={{ fontSize: "0.8125rem" }}>{value || "-"}</Typography>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      renderCell: (value: string) => truncated(value, 70),
    },
    {
      field: "claimNumber",
      headerName: "Claim No.",
      width: 110,
      renderCell: (value: string) => (
        <Typography sx={{ fontSize: "0.8125rem" }}>{value || "-"}</Typography>
      ),
    },
    {
      field: "note",
      headerName: "Note",
      flex: 1,
      renderCell: (value: string) => truncated(value, 60),
    },
    {
      field: "autoLB",
      headerName: "Auto LB",
      width: 90,
      renderCell: (value: AutoLBType | null) => (
        <Typography sx={{ fontSize: "0.8125rem" }}>
          {value != null ? AutoLBTypeLabels[value] ?? "-" : "-"}
        </Typography>
      ),
    },
    {
      field: "policeReportDescription",
      headerName: "Police Report",
      flex: 1,
      renderCell: (value: string, row: ClaimResponseDto) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
          {row.policeReportDocumentBlobId != null && (
            <Tooltip title="Police report file attached">
              <HasReportIcon fontSize="small" color="success" sx={{ flexShrink: 0 }} />
            </Tooltip>
          )}
          {truncated(value, 50)}
        </Box>
      ),
    },
    {
      field: "truckEstimate",
      headerName: "Truck Est.",
      width: 110,
      renderCell: (value: number | null) => (
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
          {formatEstimate(value)}
        </Typography>
      ),
    },
    {
      field: "trailerEstimate",
      headerName: "Trailer Est.",
      width: 110,
      renderCell: (value: number | null) => (
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
          {formatEstimate(value)}
        </Typography>
      ),
    },
    {
      field: "totalEstimate",
      headerName: "Total Estimate",
      width: 120,
      renderCell: (value: number | null) => (
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
          {formatEstimate(value)}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 155,
      renderCell: (value: ClaimStatus) => (
        <Chip
          label={ClaimStatusLabels[value] ?? "Unknown"}
          size="small"
          color={getStatusColor(value)}
          variant="outlined"
          sx={{ fontSize: "0.75rem" }}
        />
      ),
    },
    {
      field: "driverCharged",
      headerName: "Driver Charged",
      width: 135,
      renderCell: (value: DriverChargedStatus | null) =>
        value != null ? (
          <Chip
            label={DriverChargedStatusLabels[value] ?? "Unknown"}
            size="small"
            color={getDriverChargedColor(value)}
            variant="outlined"
            sx={{ fontSize: "0.75rem" }}
          />
        ) : (
          <Typography sx={{ fontSize: "0.8125rem" }}>-</Typography>
        ),
    },
  ];

  const getActions = (claim: ClaimResponseDto) => [
    {
      icon: <EditIcon fontSize="small" />,
      label: "Edit",
      onClick: () => handleEditClick(claim.id),
      color: "primary" as const,
    },
    {
      icon: <FilesIcon fontSize="small" />,
      label: "View Files",
      onClick: () => handleViewFiles(claim.id),
      color: "info" as const,
    },
    {
      icon: <DeleteIcon fontSize="small" />,
      label: "Delete",
      onClick: () => setDeleteTargetClaim(claim),
      color: "error" as const,
    },
  ];

  const statusSelectOptions = Object.entries(ClaimStatusLabels).map(
    ([value, label]) => ({
      value: Number(value),
      label,
    })
  );

  const searchableColumns: SearchableColumn[] = [
    ...makeColumnsSearchable(columns, [
      "companyName",
      "assignedToName",
      "driverName",
      "truckName",
      "trailerName",
      "claimNumber",
    ], {
      companyName: "string",
      assignedToName: "string",
      driverName: "string",
      truckName: "string",
      trailerName: "string",
      claimNumber: "string",
    }),
    {
      field: "status",
      headerName: "Status",
      searchable: true,
      searchType: "select",
      selectOptions: statusSelectOptions,
    },
  ];

  return (
    <>
      <DataTable<ClaimResponseDto>
        items={claims}
        columns={columns}
        actions={getActions}
        isLoading={isLoading}
        title="Claims"
        totalCount={totalCount}
        hasNextPage={hasNextPage}
        isLoadingMore={isLoadingMore}
        isLoadingRows={isLoadingRows}
        onLoadMore={onLoadMore}
        enableSearch={enableSearch}
        searchableColumns={searchableColumns}
        onSearchChange={onSearchChange}
      />

      {selectedClaimId && (
        <ClaimFilesDialog
          open={filesDialogOpen}
          onClose={() => {
            setFilesDialogOpen(false);
            setSelectedClaimId(null);
          }}
          claimId={selectedClaimId}
        />
      )}

      <Dialog
        open={!!deleteTargetClaim}
        onClose={() => setDeleteTargetClaim(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Claim</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete claim{" "}
            <strong>
              {deleteTargetClaim?.claimNumber
                ? `#${deleteTargetClaim.claimNumber}`
                : `#${deleteTargetClaim?.id}`}
            </strong>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTargetClaim(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClaimsTable;
